// src/pages/debitNote/DebitNoteListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileMinus, Plus, Eye, Calendar, Clock, FileText, User, CheckCircle, XCircle } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getDebitNotes } from '../../services/api/procurement/debitNote';
import { getAllSuppliers } from '../../services/api/supplier';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { PURCHASE_ORDER_BASE_PATH } from '../../utils/procurementNavigation';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './DebitNoteListPage.module.css';

// Only supplierId/purchaseOrderId are read server-side
// (rettzy-backend .../procurement/controllers/debitNote.controller.js) — no status/date-range
// params exist yet, so only those two are wired as filters here.
const STATUS_ICONS = { issued: Clock, applied: CheckCircle, cancelled: XCircle };

const statusConfig = {
  issued: { color: '#2563eb', bg: '#dbeafe' },
  applied: { color: '#10b981', bg: '#d1fae5' },
  cancelled: { color: '#ef4444', bg: '#fee2e2' },
};

// return_to_vendor debit notes are auto-issued by the backend; price_adjustment/correction
// are manually issued via this page's "Issue Debit Note" action — styled distinctly so users
// can tell system-issued and manually-issued notes apart at a glance.
const sourceTypeStyle = {
  return_to_vendor: 'muted',
  price_adjustment: 'accent',
  correction: 'accent',
};

const DebitNoteListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [debitNotes, setDebitNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ supplierId: '', purchaseOrderId: '' });

  useEffect(() => {
    (async () => {
      try {
        const result = await getAllSuppliers({ limit: 200 });
        setSuppliers(result.suppliers || []);
      } catch (err) {
        console.error('Failed to load suppliers:', err);
      }
    })();
  }, []);

  useEffect(() => {
    fetchDebitNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchDebitNotes = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      if (filters.supplierId) params.supplierId = filters.supplierId;
      if (filters.purchaseOrderId) params.purchaseOrderId = filters.purchaseOrderId;

      const response = await getDebitNotes(params);
      setDebitNotes(response.debitNotes || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch debit notes:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('debitNote.failedToLoadDebitNotes'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ supplierId: '', purchaseOrderId: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some(v => v !== '');

  const filterDefinitions = [
    {
      key: 'supplierId',
      label: t('debitNote.supplier'),
      type: 'select',
      placeholder: t('debitNote.allSuppliers'),
      options: suppliers.map(s => ({ value: s.id.toString(), label: s.name })),
    },
    {
      key: 'purchaseOrderId',
      label: t('debitNote.purchaseOrderNumber'),
      type: 'text',
      placeholder: t('debitNote.purchaseOrderIdPlaceholder'),
    },
  ];

  const columns = [
    {
      key: 'debitNoteNumber',
      header: t('debitNote.debitNoteNumber'),
      width: '170px',
      render: (row) => <DocNumberCell number={row.debitNoteNumber} to={`/procurement/debit-notes/${row.id}`} />,
    },
    {
      key: 'supplier',
      header: t('debitNote.supplier'),
      render: (row) => <div className={tb.partyInfo}><div className={tb.partyName}><User size={14} />{(row.Supplier?.name || row.supplier?.name) || t('common.na')}</div></div>,
    },
    {
      key: 'purchaseOrder',
      header: t('debitNote.purchaseOrderNumber'),
      width: '150px',
      render: (row) => row.purchaseOrder?.purchaseOrderNumber ? (
        <Link className={tb.docLink} to={`${PURCHASE_ORDER_BASE_PATH}/${row.purchaseOrder.id}`} onClick={(e) => e.stopPropagation()}>
          <FileText size={12} />{row.purchaseOrder.purchaseOrderNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'sourceType',
      header: t('debitNote.sourceType'),
      width: '150px',
      render: (row) => (
        <div className={`${styles.sourceBadge} ${styles[`source_${sourceTypeStyle[row.sourceType] || 'accent'}`]}`}>
          {t(`debitNote.sourceType_${row.sourceType}`)}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('debitNote.amount'),
      width: '120px',
      align: 'right',
      render: (row) => <div className={tb.totalAmount}>{formatCurrency(row.amount)}</div>,
    },
    {
      key: 'appliedAmount',
      header: t('debitNote.appliedAmount'),
      width: '130px',
      align: 'right',
      render: (row) => <div className={tb.amountMuted}>{formatCurrency(row.appliedAmount)}</div>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '120px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.issued;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}>
            <StatusIcon size={14} />
            <span>{t(`debitNote.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/procurement/debit-notes/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.debitNoteListPage}>
      <PageHeader
        title={t('debitNote.allDebitNotes')}
        description={t('debitNote.viewAndManageAllDebitNotes')}
        icon={FileMinus}
        actions={
          hasPermission('issue_debit_note') && (
            <Link to="/procurement/debit-notes/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('debitNote.createDebitNote')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={debitNotes}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/procurement/debit-notes/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('debitNote.noDebitNotesFound') : t('debitNote.noDebitNotesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('debitNote.noDebitNotesFoundDescription')
            : t('debitNote.noDebitNotesYetDescription')
        }
        emptyIcon={<FileMinus size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('issue_debit_note') ? (
            <Link to="/procurement/debit-notes/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('debitNote.issueFirstDebitNote')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default DebitNoteListPage;
